import useApi from 'hooks/useApi';
import { invoiceEmailsRequired, requiredCheckbox, stringRequired } from 'libs/validations';
import React, { FunctionComponent, useMemo } from 'react';
import * as Yup from 'yup';
import { PropertyInfo, YourContact } from './Cards';
import PaymentMethodLayout from './Layout';
import useAccount from 'hooks/useAccount';
import useCommon from 'hooks/useCommon';
import useOnSubmit from 'hooks/useOnSubmit';
import { FormikErrors, FormikProps } from 'formik';
import { CreditCard } from './Cards/CreditCard';
import { IEmailInvoice } from 'hooks/useEmailInvoices';
import clonedeep from 'lodash.clonedeep';
import usePayments from 'pages/Administration/PlanAndBilling/usePayments';
import get from 'lodash.get';
import { useParams } from 'react-router-dom';
import useNavigator from 'hooks/useNavigator';
import useStripeCardSetup, { IStripeCardFormValues } from 'hooks/useStripeCardSetup';
import trackAddPaymentInfo from './trackAddPaymentInfo';

export interface ICreditCardFormValues extends IStripeCardFormValues {
  is_authorized_to_payments: boolean;
  company_name: string;
  user_email: string;
  legal_name: string;
  user_name: string;
  user_position: string;
  invoice_emails_attributes: IEmailInvoice[];
}

const validationSchema = Yup.object().shape({
  invoice_emails_attributes: invoiceEmailsRequired,
  is_authorized_to_payments: requiredCheckbox,
  legal_name: stringRequired,
  user_email: stringRequired,
  user_name: stringRequired,
  user_position: stringRequired,
});

const formCards = [
  {
    component: (props: FormikProps<ICreditCardFormValues>) => <PropertyInfo {...props} />,
    title: 'Property Information',
  },
  {
    component: (props: FormikProps<ICreditCardFormValues>) => <CreditCard {...props} />,
    title: 'Credit Card Information',
  },
  {
    component: () => <YourContact />,
    title: 'Your contact information',
  },
];

const CreditCardPage: FunctionComponent = () => {
  usePayments();
  const { guid } = useParams();
  const { navigateCallback } = useNavigator();
  const { getApiPath } = useApi();
  const { plan } = useAccount();
  const { common, type, fetchCommon } = useCommon();
  const initialValues: ICreditCardFormValues = useMemo(() => {
    return {
      company_name: '',
      invoice_emails_attributes: type ? clonedeep(get(common, type).invoice_emails_attributes) : [],
      is_authorized_to_payments: false,
      legal_name: '',
      user_email: '',
      user_name: '',
      user_position: '',
      credit_card_number: '',
      credit_card_expiry: '',
      credit_card_cvc: '',
    };
  }, [type, common]);

  const onSubmit = useOnSubmit({
    api: getApiPath('billing.payment.credit', 'create'),
    deps: [guid],
    onSuccess: () => {
      trackAddPaymentInfo('credit_card', 'success');
      fetchCommon();
      navigateCallback('/dashboard')();
    },
    onError: () => trackAddPaymentInfo('credit_card', 'error'),
    params: { guid },
    serializer: (data: ICreditCardFormValues) => ({ resource: data }),
    validationErrorSerializer: (errors: FormikErrors<ICreditCardFormValues>) => ({
      ...errors,
      //@ts-ignore
      invoice_emails_attributes: errors['invoice_emails.email'],
    }),
    successMessage: 'Payment successfully saved',
  });

  const submitHandler = useStripeCardSetup<ICreditCardFormValues>({
    setupIntentApi: getApiPath('billing.payment.credit.setupIntent', 'create'),
    params: { guid },
    onSubmit,
    deps: [guid],
  });

  return (
    <PaymentMethodLayout<ICreditCardFormValues>
      plan={plan?.title}
      price={plan ? `${plan.price} / month` : ''}
      initialValues={initialValues}
      validationSchema={validationSchema}
      title="Credit Card"
      formCards={formCards}
      onSubmit={submitHandler}
    />
  );
};

export default CreditCardPage;
